// frontend/src/admin/v2/components/JobCard.jsx
import { useEffect, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Stack, Typography, Box, Chip, Button, Tooltip,
} from '@mui/material'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { SP, FS, hoverOnly, TOUCH_TARGET_COARSE } from '../../../theme/tokens'
import Panel from '../../../components/common/Panel'
import { formatBerlinDate } from '../../../utils/time'
import { formatDuration } from '../../../utils/format'
import { etikett, ton } from '../adminStatus'

function elapsed(iso) {
  if (!iso) return null
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000))
  if (s < 60) return `${s}s`
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`
}

/**
 * JobCard — one card per scheduled job on /admin/jobs.
 *
 * @description
 *   Shows label, schedule, last-run status pill + relative time, 24h stats,
 *   Run-now button, and a strip of the last N runs as colored cells.
 *
 * @props
 *   job: serialized payload from /api/admin/jobs (see backend/admin_v2/jobs.py)
 *   onRunNow: async (key) => void
 *   delay: stagger animation delay in ms
 */
// 🔴 G14a-1: die Abbildung Status → Etikett/Farbe steht seit dem 2026-10-31 an
// EINER Stelle (`adminStatus.js`), nicht mehr je Ansicht. Die Karte trug vorher
// „Failed" neben „Failing" in der Leiste — zwei Woerter fuer denselben Zustand.

const RUN_STATUS_COLOR = {
  done:      'success.main',
  error:     'error.main',
  cancelled: 'text.disabled',
  cancel_requested: 'text.disabled',
  running:   'info.main',
}

function relTime(iso) {
  if (!iso) return 'never'
  const d = new Date(iso)
  const diffMin = Math.round((Date.now() - d.getTime()) / 60_000)
  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin}m ago`
  const diffH = Math.round(diffMin / 60)
  if (diffH < 24) return `${diffH}h ago`
  return formatBerlinDate(d)
}

// EINE Quelle statt dreier Kopien — dieselbe Form wie `JobDetail`/`JobsList`.
function fmtDuration(s) {
  return formatDuration(s)
}


export default function JobCard({ job, onRunNow, delay = 0, recentRuns = [] }) {
  // `triggering`: short window between Run-now click and the next list refetch
  // populating last_run.status='running'. `liveRunning`: derived from the
  // backend's authoritative admin_sync_runs row — drives the persistent UI.
  const [triggering, setTriggering] = useState(false)
  const liveRunning = job.last_run?.status === 'running'
  const farbe = ton(job.status)
  const stats = job.stats_24h || {}
  const last = job.last_run

  // Tick a local counter once per second so the elapsed time updates while
  // the job is running. Only mounts the interval when actually needed.
  const [, setTick] = useState(0)
  useEffect(() => {
    if (!liveRunning) return
    const id = setInterval(() => setTick(t => t + 1), 1000)
    return () => clearInterval(id)
  }, [liveRunning])

  const handleRun = async (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (triggering || liveRunning) return
    setTriggering(true)
    try {
      await onRunNow(job.key)
    } finally {
      // After ~1.5s the parent useJobs poll picks up status='running' from
      // the new admin_sync_runs row; from then on liveRunning drives the UI.
      setTimeout(() => setTriggering(false), 1500)
    }
  }
  const buttonDisabled = triggering || liveRunning

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, delay: delay / 1000, ease: 'easeOut' }}
      style={{ height: '100%' }}
    >
      <Panel
        pad="card"
        data-testid={`karte-kennung-${job.key}`}
        sx={{
          position: 'relative',
          height: '100%',
          ...(liveRunning && {
            borderColor: 'info.main',
            boxShadow: theme => `0 0 0 1px ${theme.palette.info.main}`,
          }),
        }}
      >
        <Stack
          component={RouterLink}
          to={`/admin/jobs/${job.key}`}
          sx={{
            color: 'inherit',
            textDecoration: 'none',
            // 🔴 R15 W-4: der Kartenkopf ist ein LINK und damit ein Ziel. Auf
            // grobem Zeiger trägt er den 44-px-Boden wie ein Knopf.
            ...TOUCH_TARGET_COARSE,
            '&:hover .job-label': { color: 'primary.main' },
          }}
          spacing={SP.tight}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={SP.tight}>
                <Typography
                  className="job-label"
                  variant="subtitle1"
                  data-testid={`karte-titel-${job.key}`}
                  sx={{ fontSize: FS.md, fontWeight: 600, lineHeight: 1.3, transition: 'color 120ms' }}
                >
                  {job.label}
                </Typography>
                <OpenInNewIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
              </Stack>
              <Typography variant="caption" sx={{ fontSize: FS.xs, color: 'text.secondary', fontFamily: 'monospace' }}>
                {job.kind}
              </Typography>
            </Stack>
            {liveRunning ? (
              <Chip
                className="job-running-pulse"
                data-testid={`karte-status-${job.key}`}
                label={`Running… ${elapsed(last?.started_at) || '—'}`}
                size="small"
                sx={{
                  fontSize: FS.xs, fontWeight: 600,
                  color: 'info.main', bgcolor: 'transparent',
                  border: 1, borderColor: 'info.main',
                }}
              />
            ) : (
              <Chip
                data-testid={`karte-status-${job.key}`}
                label={etikett(job.status)}
                size="small"
                sx={{
                  fontSize: FS.xs, fontWeight: 600,
                  color: farbe, bgcolor: 'transparent',
                  border: 1, borderColor: farbe,
                }}
              />
            )}
          </Stack>

          <Typography variant="caption" sx={{ fontSize: FS.xs, color: 'text.secondary' }}>
            {job.schedule_desc}
          </Typography>

          {/* Trigger chain — shows what fires this job + what code runs */}
          {(job.render_service || job.cron_endpoint || job.script_path) && (
            <Stack spacing={0.25} sx={{ mt: 0.25 }}>
              {job.render_service ? (
                <Typography variant="caption" sx={{ fontSize: FS.xs, color: 'text.disabled', fontFamily: 'monospace' }}>
                  Render cron · {job.render_service}
                </Typography>
              ) : (
                <Typography variant="caption" sx={{ fontSize: FS.xs, color: 'text.disabled' }}>
                  Manual only · Run-now from /admin/jobs
                </Typography>
              )}
              {job.script_path && (
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: FS.xs, color: 'text.disabled', fontFamily: 'monospace',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}
                  title={job.script_path}
                >
                  {job.script_path}
                </Typography>
              )}
            </Stack>
          )}
        </Stack>

        {/* Last-run summary */}
        <Stack spacing={0.25} sx={{ mt: SP.card }}>
          <Stack direction="row" justifyContent="space-between" sx={{ fontSize: FS.xs, color: 'text.secondary' }}>
            <span>Last run</span>
            <span style={{ fontWeight: 500 }}>
              {last
                ? `${last.status} · ${relTime(last.started_at)}`
                : 'never run yet'}
            </span>
          </Stack>
          <Stack direction="row" justifyContent="space-between" sx={{ fontSize: FS.xs, color: 'text.secondary' }}>
            <span>24h</span>
            <span style={{ fontWeight: 500 }}>
              {stats.ok || 0}/{stats.total || 0} ok · {stats.failed || 0} failed · avg {fmtDuration(stats.avg_duration_s)}
            </span>
          </Stack>
        </Stack>

        {/* Mini run-history strip */}
        {recentRuns && recentRuns.length > 0 && (
          <Stack direction="row" spacing={0.25} sx={{ mt: SP.tight, height: 18 }}>
            {recentRuns.slice(0, 24).map((r) => (
              <Tooltip
                key={r.id}
                title={`${r.status} · ${relTime(r.started_at)}${r.duration_s != null ? ' · ' + fmtDuration(r.duration_s) : ''}`}
                arrow disableInteractive
              >
                <Box sx={{
                  flex: 1,
                  bgcolor: RUN_STATUS_COLOR[r.status] || 'action.hover',
                  borderRadius: '2px',
                  opacity: 0.85,
                }} />
              </Tooltip>
            ))}
          </Stack>
        )}

        {/* Action row */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: SP.card }}>
          <Tooltip
            title={liveRunning ? `Running… ${elapsed(last?.started_at) || ''}` : 'Trigger a run now (admin only)'}
            arrow
          >
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={
                  <PlayArrowIcon
                    className={triggering || liveRunning ? 'job-spin' : ''}
                    sx={{ fontSize: 14 }}
                  />
                }
                onClick={handleRun}
                disabled={buttonDisabled}
                sx={{ fontSize: FS.xs }}
              >
                {liveRunning ? 'Running' : (triggering ? 'Triggered' : 'Run now')}
              </Button>
            </span>
          </Tooltip>
          <Typography
            component={RouterLink}
            to={`/admin/jobs/${job.key}`}
            variant="caption"
            sx={{
              fontSize: FS.xs,
              color: 'primary.main',
              textDecoration: 'none',
              // 🔴 R15 W-4: „Details →" ist ein LINK mit eigener Trefferfläche.
              // `inline-flex` + der grobe-Zeiger-Boden machen aus der ~15-px-
              // Textzeile ein 44-px-Ziel; ohne `inline-flex` bliebe `minHeight`
              // an einem Inline-Element wirkungslos.
              display: 'inline-flex',
              alignItems: 'center',
              ...TOUCH_TARGET_COARSE,
              ...hoverOnly({ textDecoration: 'underline' }),
            }}
          >
            Details →
          </Typography>
        </Stack>
      </Panel>
      <style>{`
        .job-spin { animation: jobSpin 1.2s linear infinite; }
        @keyframes jobSpin { to { transform: rotate(360deg); } }

        .job-running-pulse { animation: jobPulse 1.6s ease-in-out infinite; }
        @keyframes jobPulse {
          0%, 100% { opacity: 1; }
          50%      { opacity: 0.55; }
        }
      `}</style>
    </motion.div>
  )
}
